<template>
  <div
    ref="tooltip"
    class="Tooltip relative group"
  >
    <div
      ref="tooltipOpener"
      class="cursor-pointer inline-block"
      @click="setVisible(!showTooltip)"
      @mouseenter="hover(true)"
      @mouseleave="hover(false)"
    >
      <slot />
    </div>
    <div
      ref="tooltipContent"
      class="tooltip type-xs"
      :style="{
        top: position.top,
        left: position.left,
      }"
      :class="{
        'hidden': !showTooltip && !permanent,
      }"
    >
      <div
        class="arrow"
        :class="`arrow${position.posY} ${position.posX}`"
        :style="{
          left: position.arrowLeft
        }"
      />
      <span v-html="text" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { onClickOutside } from '@vueuse/core';

const tooltip = useTemplateRef('tooltip');
const tooltipOpener = useTemplateRef('tooltipOpener');
const tooltipContent = useTemplateRef('tooltipContent');

type Props = {
  text: string;
  showOnHover?: boolean;

  permanent?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  showOnHover: false,
  permanent: false,
});
const showTooltip = ref(false);

if (!props.permanent) {
  onClickOutside(tooltip, () => {
    showTooltip.value = false;
  });
}

onMounted(()=> {
  findPagePosition();
  document.addEventListener('scroll', ()=> findPagePosition());
  document.addEventListener('resize', ()=> findPagePosition());
});
onBeforeUnmount(()=> {
  document.removeEventListener('scroll', ()=> findPagePosition());
  document.removeEventListener('resize', ()=> findPagePosition());
});

const hover = (set: boolean) => {
  if (props.showOnHover) {
    setVisible(set);
  }
};

const setVisible = (set: boolean) => {
  showTooltip.value = set;
  if (set) {
    setTimeout(()=>findPagePosition(), 25);
  }
};

const position = ref({
  top: '',
  left: '',
  posY: '',
  posX: '',
  arrowLeft: '',
});

const findPagePosition = () => {
  if (showTooltip.value) {
    const opener = tooltipOpener.value?.getBoundingClientRect();
    const content = tooltipContent.value?.getBoundingClientRect();
    if (opener && content) {
      const openerTop = opener.top;
      const openerCenter = opener.left - opener.width / 2;
      const openerHeight = opener.height;

      const contentH = content?.height || 0;
      const contentW = content?.width || 1;
      const contentL = content?.left || 0;

      let posY = 'Top';
      let posX = '';
      let outT = openerTop - contentH - 10;
      let outL = openerCenter - contentW / 2;
      let arrowLeft = 'calc(50% + 5px)';

      // move to bottom
      if (outT < 0) {
        posY = 'Bottom';
        outT = openerTop + openerHeight + 10;
      }

      if (openerCenter < contentW / 2) {
        const moveR = contentW / 2 - openerCenter;
        outL = 0;
        const moveToL = contentW / 2 - moveR + 5;
        arrowLeft = `${Math.max(moveToL, 12)}px`;
      }

      // if position is absoulute right
      if (contentL + Math.max(contentW, 2) >= window.innerWidth) {
        if (contentL + Math.max(contentW, 2) > window.innerWidth) {
          outL = window.innerWidth - Math.max(contentW, 2);
        }
        arrowLeft = `${openerCenter - outL + 5}px`;
      }

      position.value = {
        top: `${outT}px`,
        left: `${outL}px`,
        posY,
        posX,
        arrowLeft,
      };
    }
  }
};

</script>

<style scoped>
.tooltip {
  @apply fixed min-w-120 max-w-360 bg-bg-inverse text-text-inverse p-12 z-modalsContent text-center rounded-6;
}

.arrow {
  @apply absolute w-0 h-0 z-below ;
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  transform: translateX(-50%);
}
.arrowTop {
  @apply -bottom-[19px];
  border-bottom: 10px solid transparent;
  border-top: 10px solid #2e2d2b;
}
.arrowBottom {
  @apply -top-[19px];
  border-top: 10px solid transparent;
  border-bottom: 10px solid #2e2d2b;
}

</style>
